import { useMemo } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Layers, Plus } from 'lucide-react'

import type { ListLotsParams } from '@/lib/api/agri-material-lot-api'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { defineListFilters } from '@/components/common/filters/core'
import { textFilter } from '@/components/common/filters/text-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import {
    LOT_CARD_CODE_CLASS,
    LotListCard,
} from '@/components/common/LotListCard'
import { agriUnitLabel } from '@/components/agricultural-materials/agri-unit-label'
import { Button } from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import { useIsMobile } from '@/hooks/use-is-mobile'
import {
    useAgriLots,
    useAgriLotsInfinite,
} from '@/hooks/use-agri-material-lot-query'
import { formatDateTimeVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

const PERMISSION = 'agri_material_lots.read'
const PER_PAGE = 50

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * Ô tìm kiếm trước đây nằm trong `useState` nên không vào URL và cũng không có
 * nhãn — chỉ có placeholder (R7, R9).
 */
const FILTERS = defineListFilters({
    q: textFilter({
        label: m.aml_lots_search_label,
        placeholder: m.aml_lots_search_ph,
    }),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/agricultural-materials/lots/',
)({
    component: AgriLotsPage,
    validateSearch: FILTERS.validateSearch,
})

function AgriLotsPage() {
    const { hasPermission, arePermissionsLoaded } = useAuth()
    if (arePermissionsLoaded && !hasPermission(PERMISSION)) {
        return (
            <div className="p-6 text-sm text-muted-foreground">
                {m.aml_no_permission_view()}
            </div>
        )
    }
    return <LotsContent />
}

function LotsContent() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('agri_material_lots.write')
    const navigate = useNavigate({ from: '/agricultural-materials/lots/' })
    const { values, barProps, page, setPage } = useListFilters({
        def: FILTERS,
        search: Route.useSearch(),
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 3,
    })
    const isMobile = useIsMobile()

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const params = useMemo<ListLotsParams>(
        () => ({
            page,
            per_page: PER_PAGE,
            ...(values.q ? { search: values.q } : {}),
        }),
        [page, values.q],
    )
    const { data, isLoading, isFetching, refetch } = useAgriLots(params, {
        enabled: !isMobile,
    })

    const infiniteParams = useMemo(
        () => ({
            per_page: PER_PAGE,
            ...(values.q ? { search: values.q } : {}),
        }),
        [values.q],
    )
    const infinite = useAgriLotsInfinite(infiniteParams, { enabled: isMobile })
    const mobileLots = useMemo(
        () => infinite.data?.pages.flatMap((p) => p.data ?? []) ?? [],
        [infinite.data],
    )

    const lots = data?.data ?? []
    const total = data?.meta?.total ?? 0
    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {values.q ? m.list_empty_filtered() : m.aml_lots_empty()}
            </span>
            {values.q ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                icon={<Layers className="size-5 shrink-0" />}
                title={m.aml_lots_title()}
                primaryActions={
                    canWrite ? (
                        <Button asChild>
                            <Link
                                to="/agricultural-materials/stock-in-notes/new"
                                className="w-full"
                            >
                                <Plus className="mr-1 size-4" />
                                {m.aml_notes_new_pnk()}
                            </Link>
                        </Button>
                    ) : undefined
                }
            />

            <ListFilterBar
                {...barProps}
                onRefresh={() => void refetch()}
                refreshing={isFetching}
            />

            {/* Mobile: card list + Xem thêm */}
            <div className="space-y-2 md:hidden">
                {infinite.isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.aml_loading()}
                    </div>
                ) : mobileLots.length === 0 ? (
                    emptyState
                ) : (
                    <>
                        {mobileLots.map((lot) => (
                            // Task 1: ẩn mã lô + HSD — card hiển thị vật tư + kho
                            // + số lượng (tồn).
                            <LotListCard
                                key={lot.id}
                                onClick={() =>
                                    void navigate({
                                        to: '/agricultural-materials/lots/$id',
                                        params: { id: lot.id },
                                    })
                                }
                                code={
                                    <Link
                                        to="/agricultural-materials/lots/$id"
                                        params={{ id: lot.id }}
                                        className={LOT_CARD_CODE_CLASS}
                                    >
                                        {lot.material_name ?? '—'}
                                    </Link>
                                }
                                attribute={lot.warehouse_name ?? '—'}
                                quantityAvailable={lot.quantity_available}
                                quantityUnit={agriUnitLabel(lot.material_unit)}
                                createdAt={lot.created_at}
                            />
                        ))}
                        {infinite.hasNextPage && (
                            <Button
                                variant="outline"
                                className="h-11 w-full"
                                disabled={infinite.isFetchingNextPage}
                                onClick={() => void infinite.fetchNextPage()}
                            >
                                {infinite.isFetchingNextPage
                                    ? m.list_loading_more()
                                    : m.list_load_more()}
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden overflow-x-auto rounded-md border bg-card md:block">
                <Table>
                    <TableHeader>
                        {/* Task 1: ẩn cột mã lô + HSD — trang lô là view tồn
                            theo số lượng, chỉ còn vật tư + kho + số lượng. */}
                        <TableRow>
                            <TableHead>{m.aml_lots_col_material()}</TableHead>
                            <TableHead>{m.aml_lots_col_warehouse()}</TableHead>
                            <TableHead className="text-right">
                                {m.aml_lots_col_available()}
                            </TableHead>
                            <TableHead>{m.aml_lots_col_created()}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={4}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.aml_loading()}
                                </TableCell>
                            </TableRow>
                        ) : lots.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4}>{emptyState}</TableCell>
                            </TableRow>
                        ) : (
                            lots.map((lot) => (
                                <TableRow
                                    key={lot.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() =>
                                        void navigate({
                                            to: '/agricultural-materials/lots/$id',
                                            params: { id: lot.id },
                                        })
                                    }
                                >
                                    <TableCell>
                                        <Link
                                            to="/agricultural-materials/lots/$id"
                                            params={{ id: lot.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {lot.material_name}
                                        </Link>
                                    </TableCell>
                                    <TableCell>{lot.warehouse_name}</TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {formatQty(lot.quantity_available)}{' '}
                                        {agriUnitLabel(lot.material_unit)}
                                    </TableCell>
                                    <TableCell className="text-sm text-muted-foreground">
                                        {formatDateTimeVN(lot.created_at)}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination — desktop/tablet only */}
            <div className="hidden items-center justify-between md:flex">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(Math.max(1, page - 1))}
                >
                    {m.sl_pagination_prev()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.sl_pagination_page({
                        page,
                        total: Math.max(1, Math.ceil(total / PER_PAGE)),
                    })}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!total || page * PER_PAGE >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.sl_pagination_next()}
                </Button>
            </div>
        </div>
    )
}
